import React, { useState, useReducer, useEffect } from 'react';
import { useAllUsersAreAdminsEnabled } from '@trello/organizations';
import {
  useMoonshotCreateTeamSubmitMutation,
  useMoonshotCopyBoardMutation,
  useMoonshotInviteTeamMembersMutation,
} from '@trello/graphql/generated';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { logError } from 'app/scripts/debug/log-error';
import { Team } from 'app/scripts/debug/constants';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { MemberResponse } from 'app/gamma/src/types/responses';
import { Spinner } from 'app/src/components/Spinner';
import { MoonshotBox } from './MoonshotBox';
import { MoonshotCreateTeamForm } from './MoonshotCreateTeamForm';
import { MoonshotInviteTeamMembers } from './MoonshotInviteTeamMembers';
import { MoonshotSubmitFunction } from './types';
import { MoonshotSteps } from 'app/common/data/campaigns';
import styles from './MoonshotCreateTeamInviteForm.less';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { Analytics } from '@trello/atlassian-analytics';
import { forNamespace } from 'app/common/lib/util/i18n';

const format = forNamespace(['moonshot', 'create team']);

const TEAM_ERROR_MSG = format('team name input error');
const CATEGORY_ERROR_MSG = format('team type select error');
const ERROR_MESSAGE = format('invite error');

const boardsToCopy = [
  // Team Goal Tracker
  '5d6040a01b9d63548b2edaf9',
  // Manage any project
  '5d6040b0c2aa3e110a798545',
  // Run better meetings
  '5d6040bd99aa5878d2e7174b',
  // Keep Team Info Organized
  '5d6040ca785f435af362b6c6',
];

const memberReducer = (
  state: MemberResponse[],
  action:
    | {
        type: 'add';
        member: MemberResponse;
      }
    | {
        type: 'remove';
        memberIndex: number;
      },
) => {
  switch (action.type) {
    case 'add':
      return [...state, action.member];
    case 'remove':
      return state.filter((_, i) => i !== action.memberIndex);
    default:
      return state;
  }
};

const errorTimeout = 5000;

interface Props {
  onSubmit: MoonshotSubmitFunction;
}

export const MoonshotCreateTeamInviteForm: React.FC<Props> = ({ onSubmit }) => {
  const [teamName, setTeamName] = useState('');
  const [teamNameError, setTeamNameError] = useState('');
  const [category, setCategory] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [orgId, setOrgId] = useState('');

  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);

  const allAdminsEnabled = useAllUsersAreAdminsEnabled();

  const [
    createOrganization,
    { loading: creating },
  ] = useMoonshotCreateTeamSubmitMutation();

  const trimmedName = teamName.trim();
  const [copyBoardToOrg] = useMoonshotCopyBoardMutation();
  const createTeam = async () => {
    if (creating) {
      return;
    }

    const teamNameHasError = trimmedName.length === 0;
    const categoryHasError = category.length === 0;

    if (teamNameHasError) {
      setTeamNameError(TEAM_ERROR_MSG);
    }

    if (categoryHasError) {
      setCategoryError(CATEGORY_ERROR_MSG);
    }

    if (teamNameHasError || categoryHasError) {
      return;
    }

    let result;
    try {
      const { data } = await createOrganization({
        variables: {
          type: 'default',
          displayName: trimmedName,
          allAdminsEnabled,
        },
      });
      result = data;
    } catch (err) {
      Alerts.showLiteralText(err.message, 'error', 'alert', errorTimeout);
      logError({
        error: err,
        tags: { ownershipArea: Team.BizTeam },
      });
    }

    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.CREATES,
      directObj: Noun.TEAM,
      context: { vertical: category },
    });

    if (sendGASEvent) {
      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'inviteTeamMembersButton',
        source: 'moonshotCreateTeamScreen',
        attributes: {
          vertical: category,
        },
      });
    }

    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SUBMITS,
      directObj: Noun.TEAM_CREATION,
      method: 'by clicking next button',
      context: { currentStep: 'create-team' },
    });

    if (sendGASEvent) {
      Analytics.sendTrackEvent({
        action: 'created',
        actionSubject: 'team',
        source: 'moonshotCreateTeamScreen',
        attributes: {
          vertical: category,
        },
        containers: {
          organization: {
            id: orgId,
          },
        },
      });
    }

    if (result?.createOrganization) {
      const organizationId = result.createOrganization.id;
      try {
        await Promise.all(
          boardsToCopy.map((boardId) =>
            copyBoardToOrg({
              variables: {
                boardId,
                organizationId,
              },
            }),
          ),
        );
      } catch (err) {
        logError({
          error: err,
          tags: { ownershipArea: Team.BizTeam },
        });
      }

      setOrgId(organizationId);
      return organizationId;
    }
  };

  const [addMembersToOrg] = useMoonshotInviteTeamMembersMutation();
  const [selectedMembers, updateMembers] = useReducer(memberReducer, []);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [invitingMembers, setIsInvitingMembers] = useState(false);

  const inviteMembers = async (organizationId: string) => {
    if (invitingMembers) {
      return;
    }

    setIsInvitingMembers(true);

    if (selectedMembers.length) {
      let errorMsg = '';

      try {
        const { data: addMembersData } = await addMembersToOrg({
          variables: {
            orgId: organizationId,
            users: selectedMembers,
            invitationMessage,
          },
        });

        if (addMembersData?.addMembersToOrg?.error) {
          errorMsg = ERROR_MESSAGE;
        }
      } catch (err) {
        errorMsg = ERROR_MESSAGE;
      }

      if (errorMsg) {
        Alerts.showLiteralText(errorMsg, 'error', 'alert', errorTimeout);
      }
    }

    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SENDS,
      directObj: Noun.TEAM_INVITATION,
      context: { numInvited: selectedMembers.length },
    });
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SUBMITS,
      directObj: Noun.TEAM_INVITATION,
      method: 'by clicking next button',
      context: { currentStep: 'invite' },
    });

    if (sendGASEvent) {
      Analytics.sendTrackEvent({
        action: 'sent',
        actionSubject: 'invitation',
        actionSubjectId: 'teamInvitation',
        source: 'moonshotInviteMembersScreen',
        attributes: {
          numInvited: selectedMembers.length,
        },
      });
    }

    setIsInvitingMembers(false);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (invitingMembers || creating) {
      setIsSubmitting(true);
    }
  }, [invitingMembers, creating]);

  const submit: React.FormEventHandler = async (e) => {
    e.preventDefault();

    const organizationId = await createTeam();

    if (organizationId) {
      await inviteMembers(organizationId);

      // organizationID only exists if the team creation fields were validated
      // and the team was sucessfully created otherwise it'll be undefined
      // we don't want to move to the next step if the team isn't made, so we call onSubmit in here.
      onSubmit(MoonshotSteps.PAYMENT);
    }
  };

  return (
    <MoonshotBox>
      <form className={styles.container} onSubmit={submit}>
        <MoonshotCreateTeamForm
          onChangeTeamName={(e) => {
            setTeamName(e.target.value);
            setTeamNameError('');
          }}
          onChangeCategory={({ value }) => {
            setCategory(value);
            setCategoryError('');
          }}
          teamName={teamName}
          teamNameError={teamNameError}
          categoryError={categoryError}
        />
        <MoonshotInviteTeamMembers
          orgId={orgId}
          orgName={trimmedName}
          updateMembers={updateMembers}
          selectedMembers={selectedMembers}
          setInvitationMessage={setInvitationMessage}
          invitationMessage={invitationMessage}
          isInvitingMembers={invitingMembers}
        />
        <button id="submitButton" type="submit" className={styles.submitButton}>
          {isSubmitting ? (
            <Spinner centered wrapperClassName={styles.spinner} />
          ) : (
            format('continue button')
          )}
        </button>
      </form>
    </MoonshotBox>
  );
};
